import {
  SPEED_TO_LEAD_DEFAULTS,
  type NoAnswerAction,
  type SpeedToLeadSettings,
  type SpeedToLeadStaffEntry,
} from '../../types/speedToLead.js';
import { resolveInboundCalls, validateInboundCallsShape } from './inboundCalls.js';
import { resolveCalls, validateCallsShape } from './callSettings.js';

// Speed to Lead club settings: read boundary, write validation, and the one place the Stage 2 lock lives.
//
// ⚠️ EVERYTHING DEFAULTS OFF. A club with no settings doc resolves to Sam off and calls off.
// ⚠️⚠️ `speedToLeadEnabled` (Stage 3) is OFF by default and turned on only by a person saving the form, one
// club at a time. Even then `effectiveSpeedToLead` is false unless the GLOBAL kill switch
// (SPEED_TO_LEAD_CALLS_ENABLED) is on, so no setting can place a call by itself.

export const STAFF_LIST_MAX = 30;

export const SETTINGS_LIMITS = {
  fallbackMinutes: { min: 1, max: 120 },
  qualityCallSeconds: { min: 5, max: 600 },
  staffRingSeconds: { min: 10, max: 60 },
  roundTwoDelaySeconds: { min: 0, max: 1800 },
  maxRounds: { min: 1, max: 5 },
  retryAfterMinutes: { min: 1, max: 1440 },
} as const;

function int(value: unknown, fallback: number, min: number, max: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max ? value : fallback;
}

function staffListOf(raw: unknown): SpeedToLeadStaffEntry[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: SpeedToLeadStaffEntry[] = [];
  for (const e of raw) {
    const id =
      typeof (e as { teamMemberId?: unknown })?.teamMemberId === 'string'
        ? (e as { teamMemberId: string }).teamMemberId.trim()
        : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ teamMemberId: id, receivesLeadCalls: (e as { receivesLeadCalls?: unknown }).receivesLeadCalls === true });
  }
  return out.slice(0, STAFF_LIST_MAX);
}

/**
 * The read boundary. A missing doc and a garbage field both resolve to the DEFAULT (off), never to
 * something that reads as on. ⚠️ A stored non-boolean for an enable flag is false, not truthy.
 */
export function resolveSettings(
  accountId: string,
  raw: Partial<SpeedToLeadSettings> | null | undefined,
): SpeedToLeadSettings {
  const r = raw ?? {};
  const D = SPEED_TO_LEAD_DEFAULTS;
  const L = SETTINGS_LIMITS;
  const retry = r.retryAfterMinutes;
  return {
    accountId,
    samEnabled: r.samEnabled === true,
    speedToLeadEnabled: r.speedToLeadEnabled === true,
    botConfigId: typeof r.botConfigId === 'string' && r.botConfigId.trim() !== '' ? r.botConfigId : null,
    respectStaffedHours: r.respectStaffedHours === false ? false : D.respectStaffedHours,
    fallbackMinutes: int(r.fallbackMinutes, D.fallbackMinutes, L.fallbackMinutes.min, L.fallbackMinutes.max),
    qualityCallSeconds: int(r.qualityCallSeconds, D.qualityCallSeconds, L.qualityCallSeconds.min, L.qualityCallSeconds.max),
    // The read boundary IS the migration: a stored `staff_voicemail` (retired 2026-10-23) reads as `hang_up`.
    noAnswerAction: D.noAnswerAction as NoAnswerAction,
    retryAfterMinutes:
      typeof retry === 'number' &&
      Number.isInteger(retry) &&
      retry >= L.retryAfterMinutes.min &&
      retry <= L.retryAfterMinutes.max
        ? retry
        : null,
    staffRingSeconds: int(r.staffRingSeconds, D.staffRingSeconds, L.staffRingSeconds.min, L.staffRingSeconds.max),
    roundTwoDelaySeconds: int(
      r.roundTwoDelaySeconds,
      D.roundTwoDelaySeconds,
      L.roundTwoDelaySeconds.min,
      L.roundTwoDelaySeconds.max,
    ),
    maxRounds: int(r.maxRounds, D.maxRounds, L.maxRounds.min, L.maxRounds.max),
    staffList: staffListOf(r.staffList),
    // ⚠️ Absent or garbage = the defaults, which are OFF.
    inboundCalls: resolveInboundCalls(r.inboundCalls),
    calls: resolveCalls(r.calls),
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : '',
    updatedBy: typeof r.updatedBy === 'string' ? r.updatedBy : '',
    updatedByName: typeof r.updatedByName === 'string' ? r.updatedByName : null,
  };
}

/** The editable fields of a settings write (everything but the audit stamps). */
export type SettingsInput = Omit<SpeedToLeadSettings, 'accountId' | 'updatedAt' | 'updatedBy' | 'updatedByName' | 'inboundCalls' | 'calls'> & {
  /** Optional: a write that omits it KEEPS the stored block (the service decides), never resets it. */
  inboundCalls?: SpeedToLeadSettings['inboundCalls'];
  /** Optional: omitted KEEPS the stored block (the service decides), never resets it. */
  calls?: SpeedToLeadSettings['calls'];
};

export type SettingsValidation = { ok: true; value: SettingsInput } | { ok: false; status: 400; error: string };

function bad(error: string): SettingsValidation {
  return { ok: false, status: 400, error };
}

/**
 * ⚠️ ONE validator for the form and the API. Strict about types (no coercion: "5" is not 5).
 */
export function validateSettingsInput(input: unknown): SettingsValidation {
  if (!input || typeof input !== 'object') return bad('Settings are missing.');
  const i = input as Record<string, unknown>;
  const L = SETTINGS_LIMITS;
  if (typeof i.samEnabled !== 'boolean') return bad('samEnabled must be true or false.');
  if (typeof i.speedToLeadEnabled !== 'boolean') return bad('speedToLeadEnabled must be true or false.');
  if (typeof i.respectStaffedHours !== 'boolean') return bad('respectStaffedHours must be true or false.');
  if (i.botConfigId !== null && typeof i.botConfigId !== 'string') return bad('botConfigId must be a bot id or null.');
  const ranged: Array<[keyof typeof L, string]> = [
    ['fallbackMinutes', 'Fallback minutes'],
    ['qualityCallSeconds', 'Quality call seconds'],
    ['staffRingSeconds', 'Staff ring seconds'],
    ['roundTwoDelaySeconds', 'Round two delay'],
    ['maxRounds', 'Max rounds'],
  ];
  for (const [key, label] of ranged) {
    const v = i[key];
    if (typeof v !== 'number' || !Number.isInteger(v) || v < L[key].min || v > L[key].max) {
      return bad(`${label} must be a whole number from ${L[key].min} to ${L[key].max}.`);
    }
  }
  if (i.noAnswerAction !== 'hang_up' && i.noAnswerAction !== 'staff_voicemail') {
    return bad('noAnswerAction must be hang_up or staff_voicemail.');
  }
  const retry = i.retryAfterMinutes;
  if (
    retry !== null &&
    (typeof retry !== 'number' ||
      !Number.isInteger(retry) ||
      retry < L.retryAfterMinutes.min ||
      retry > L.retryAfterMinutes.max)
  ) {
    return bad(
      `Retry after must be empty or a whole number of minutes from ${L.retryAfterMinutes.min} to ${L.retryAfterMinutes.max}.`,
    );
  }
  if (!Array.isArray(i.staffList) || i.staffList.length > STAFF_LIST_MAX) {
    return bad(`The staff list can hold up to ${STAFF_LIST_MAX} people.`);
  }
  const seen = new Set<string>();
  const staffList: SpeedToLeadStaffEntry[] = [];
  for (const e of i.staffList) {
    const id =
      typeof (e as { teamMemberId?: unknown })?.teamMemberId === 'string'
        ? (e as { teamMemberId: string }).teamMemberId.trim()
        : '';
    if (!id || typeof (e as { receivesLeadCalls?: unknown }).receivesLeadCalls !== 'boolean') {
      return bad('Each staff entry needs a team member and an on/off setting.');
    }
    if (seen.has(id)) return bad('A team member appears twice in the staff list.');
    seen.add(id);
    staffList.push({ teamMemberId: id, receivesLeadCalls: (e as { receivesLeadCalls: boolean }).receivesLeadCalls });
  }
  let inboundCalls: SettingsInput['inboundCalls'];
  if (i.inboundCalls !== undefined) {
    const v = validateInboundCallsShape(i.inboundCalls);
    if (!v.ok) return bad(v.error);
    inboundCalls = v.value;
  }
  let calls: SettingsInput['calls'];
  if (i.calls !== undefined) {
    const v = validateCallsShape(i.calls);
    if (!v.ok) return bad(v.error);
    calls = v.value;
  }
  return {
    ok: true,
    value: {
      ...(inboundCalls ? { inboundCalls } : {}),
      ...(calls ? { calls } : {}),
      samEnabled: i.samEnabled,
      speedToLeadEnabled: i.speedToLeadEnabled,
      botConfigId: (i.botConfigId as string | null) ?? null,
      respectStaffedHours: i.respectStaffedHours,
      fallbackMinutes: i.fallbackMinutes as number,
      qualityCallSeconds: i.qualityCallSeconds as number,
      noAnswerAction: 'hang_up',
      retryAfterMinutes: retry as number | null,
      staffRingSeconds: i.staffRingSeconds as number,
      roundTwoDelaySeconds: i.roundTwoDelaySeconds as number,
      maxRounds: i.maxRounds as number,
      staffList,
    },
  };
}

/**
 * Whether the call channel counts as on: the club's own switch AND the GLOBAL kill switch (`callsAvailable` =
 * config.speedToLeadCallsEnabled, default false). ⚠️ Nothing but a person ever sets the club switch, and the
 * kill switch beats it.
 */
export function effectiveSpeedToLead(
  settings: Pick<SpeedToLeadSettings, 'speedToLeadEnabled'>,
  callsAvailable: boolean,
): boolean {
  return callsAvailable && settings.speedToLeadEnabled === true;
}

/** The staff-list-only validation (the portal's one write). */
export function validateStaffListOnly(
  staffList: unknown,
): { ok: true; value: SpeedToLeadStaffEntry[] } | { ok: false; error: string } {
  const probe = validateSettingsInput({
    ...SPEED_TO_LEAD_DEFAULTS,
    botConfigId: null,
    retryAfterMinutes: null,
    staffList,
  });
  if (!probe.ok) return { ok: false, error: probe.error };
  return { ok: true, value: probe.value.staffList };
}
